import type { CSSProperties } from 'react';
import styles from './QuillLoader.module.css';

/**
 * The spoken-word waveform the quill writes, in the 280 × 104 scene's px
 * (a smoothed envelope of two phrases, 12 → 268 along the 52 px midline).
 * index.html's static splash carries the same path.
 */
export const QUILL_WAVE_PATH =
  'M12 52 C12.7 52 14.7 51.9 16 51.8 C17.3 51.6 18.7 51.3 20 51.2 C21.3 51.1 22.7 50.9 24 50.9 C25.3 50.9 26.7 51.1 28 51.4 C29.3 51.8 30.7 52.4 32 53.1 C33.3 53.7 34.7 54.7 36 55.4 C37.3 56 38.7 56.8 40 56.9 C41.3 56.9 42.7 56.7 44 55.7 C45.3 54.6 46.7 52.6 48 50.6 C49.3 48.7 50.7 45.6 52 43.8 C53.3 42 54.7 40.1 56 39.8 C57.3 39.4 58.7 40.1 60 41.9 C61.3 43.8 62.7 47.4 64 50.8 C65.3 54.2 66.7 59.1 68 62.3 C69.3 65.5 70.7 68.9 72 69.8 C73.3 70.8 74.7 70.3 76 68.2 C77.3 66.1 78.7 61.6 80 57.4 C81.3 53.3 82.7 47.3 84 43.5 C85.3 39.6 86.7 35.7 88 34.3 C89.3 32.9 90.7 33.3 92 35 C93.3 36.7 94.7 40.8 96 44.4 C97.3 48 98.7 53.2 100 56.6 C101.3 60 102.7 63.3 104 64.8 C105.3 66.3 106.7 66.4 108 65.5 C109.3 64.6 110.7 61.9 112 59.3 C113.3 56.8 114.7 53 116 50.1 C117.3 47.3 118.7 44.1 120 42.4 C121.3 40.6 122.7 39.4 124 39.6 C125.3 39.7 126.7 41.2 128 43.4 C129.3 45.5 130.7 49.3 132 52.6 C133.3 55.9 134.7 60.4 136 63.1 C137.3 65.9 138.7 68.6 140 69 C141.3 69.4 142.7 68.3 144 65.5 C145.3 62.8 146.7 57.5 148 52.7 C149.3 48 150.7 41.1 152 36.9 C153.3 32.7 154.7 28.5 156 27.7 C157.3 26.9 158.7 28.7 160 32.3 C161.3 36 162.7 43.4 164 49.7 C165.3 56 166.7 64.9 168 70.1 C169.3 75.3 170.7 80 172 80.8 C173.3 81.5 174.7 78.9 176 74.6 C177.3 70.3 178.7 61.7 180 55 C181.3 48.3 182.7 39.2 184 34.2 C185.3 29.2 186.7 25.3 188 25 C189.3 24.6 190.7 27.9 192 32.1 C193.3 36.2 194.7 44 196 49.7 C197.3 55.4 198.7 62.5 200 66.3 C201.3 70 202.7 72.3 204 72.2 C205.3 72.1 206.7 68.9 208 65.7 C209.3 62.5 210.7 56.8 212 52.9 C213.3 49 214.7 44.6 216 42.4 C217.3 40.2 218.7 39.3 220 39.6 C221.3 39.9 222.7 42.3 224 44.3 C225.3 46.4 226.7 49.9 228 52.1 C229.3 54.4 230.7 56.8 232 57.8 C233.3 58.8 234.7 58.5 236 58.1 C237.3 57.6 238.7 56.1 240 55 C241.3 53.9 242.7 52.5 244 51.6 C245.3 50.8 246.7 50.2 248 49.9 C249.3 49.7 250.7 49.9 252 50.1 C253.3 50.4 254.7 50.9 256 51.3 C257.3 51.6 258.7 52 260 52.2 C261.3 52.4 262.7 52.4 264 52.3 C265.3 52.3 267.3 52.1 268 52';

/**
 * Timeline (ms from the loader's start): the scene fades in after `enterDelay`,
 * the quill starts writing at `writeDelay` and loops every `cycle`. Within a
 * cycle it writes for the first 70 %, lifts off by 82 % while the ink holds
 * until 84 %, then the ink fades and the quill comes back to the start.
 */
export const QUILL_TIMING = { enterDelay: 120, writeDelay: 240, cycle: 3400, signedAt: 0.84 } as const;

/** When the first stroke is complete: written in full and the quill lifted off (3096 ms). */
export const QUILL_SIGNATURE_MS = QUILL_TIMING.writeDelay + QUILL_TIMING.signedAt * QUILL_TIMING.cycle;

export interface QuillLoaderProps {
  /** Phase message under the line ("Preparing your library...", "Opening your library..."). */
  message: string;
  /** How far into its timeline the loader starts, to continue an animation already on screen (the static splash's). */
  elapsedMs?: number;
}

/**
 * The startup loader (replaces Flutter's AppLoadingSkeleton card): a quill
 * writes a spoken-word waveform over its faint guide, lifts off as the ink
 * fades, and writes it again. Reduced motion shows the finished line.
 */
export function QuillLoader({ message, elapsedMs = 0 }: QuillLoaderProps) {
  const timeline = {
    '--quill-offset': `${-Math.max(0, elapsedMs)}ms`,
    '--quill-enter-delay': `${QUILL_TIMING.enterDelay}ms`,
    '--quill-write-delay': `${QUILL_TIMING.writeDelay}ms`,
    '--quill-cycle': `${QUILL_TIMING.cycle}ms`,
  } as CSSProperties;
  return (
    <div className={styles.root} style={timeline} role="status" aria-label="Loading screen">
      <div className={styles.scene} aria-hidden>
        <svg className={styles.wave} width="280" height="104" viewBox="0 0 280 104" fill="none">
          <path className={styles.guide} d={QUILL_WAVE_PATH} />
          <path className={styles.ink} d={QUILL_WAVE_PATH} pathLength={100} />
        </svg>
        <div className={styles.quill} style={{ offsetPath: `path('${QUILL_WAVE_PATH}')` }}>
          <svg width="34" height="34" viewBox="0 0 32 32" fill="none">
            <path className={styles.vane} d="M8.5 23.5C8 15.5 15 6.5 29 3C26.5 15.5 17.5 23 8.5 23.5Z" />
            <path className={styles.notches} d="M9.5 17.4L10.6 18.5M13.3 11.6L14.5 12.8M18.9 7.1L20 8.2M23.3 15.5L22.1 14.3" />
            <path className={styles.shaft} d="M3 29L26 6" />
          </svg>
        </div>
      </div>
      <p className={styles.message}>{message}</p>
    </div>
  );
}
